/**
 * "Manis featuring this" section on CommunityItemDetailPage.
 *
 * Fetches creations that listed the current item (any itemType) in their
 * `itemsUsed`, filters out private/deleted, and renders each as a
 * mini-card with creator attribution. Renders nothing when the fetch
 * returns empty — no "coming soon" copy needed because the section only
 * makes sense when there's content.
 *
 * Independently fetched from the item aggregation so a slow or failed
 * creations query doesn't block the page. Uses the `itemsUsedRefs`
 * COLLECTION_GROUP array-contains index; see
 * services/communityItemService.ts for the query and
 * utils/creationRefs.ts for the ref format.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getCreationsFeaturingItem,
  type CreationFeaturing,
} from '../../services/communityItemService';
import type { ItemType } from '../../types';

interface Props {
  itemType: ItemType;
  brandSlug: string;
  nameSlug: string;
}

export default function CreationsFeaturingThis({ itemType, brandSlug, nameSlug }: Props) {
  const [creations, setCreations] = useState<CreationFeaturing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCreationsFeaturingItem(itemType, brandSlug, nameSlug)
      .then((results) => {
        if (!cancelled) {
          setCreations(results);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('getCreationsFeaturingItem failed:', err);
        if (!cancelled) {
          setCreations([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [itemType, brandSlug, nameSlug]);

  // Nothing to render pre-fetch; keeps the page shell clean while the
  // rest of the detail-page components paint.
  if (loading) return null;
  if (creations.length === 0) return null;

  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-glass mb-3">Creations featuring this</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {creations.map((c) => (
          <article
            key={c.id}
            className="bg-slate-800/50 border border-slate-600 rounded-lg overflow-hidden"
          >
            {c.photoUrl ? (
              <div className="aspect-square bg-slate-900">
                <img
                  src={c.photoUrl}
                  alt={c.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="aspect-square bg-slate-900 flex items-center justify-center text-4xl" aria-hidden="true">
                💅
              </div>
            )}
            <div className="p-3">
              <h3 className="text-sm font-medium text-glass truncate" title={c.title}>
                {c.title}
              </h3>
              <Link
                to={`/profile/${c.username || c.userId}`}
                className="text-xs text-glass-muted hover:text-glass truncate block mt-0.5"
              >
                by @{c.username ?? c.displayName ?? c.userId.slice(0, 6)}
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
